<script lang="ts" module>
  // IconButton — the design-system compact icon-button primitive
  // (wiki/feature-design-system.md §Component primitives, AC4). A small SQUARE
  // button carrying a single icon — the Archetype step controls, the dialog
  // dismiss ×, the delivery-plan rename/delete pair. Distinct from the text
  // `Button`: compact, square, icon-only, with a REQUIRED `label` (the icon
  // alone is not an accessible name).
  //
  // The icon rides in as a TYPED COMPONENT PROP (`icon={Pencil}` — a Lucide
  // component), never a slot, per the project-wide composition rule. The
  // `children` snippet stays for the plain text glyphs (×, ↑, ?) that need no
  // icon module.
  //
  // `title` renders as a tooltip shown on hover AND on keyboard focus — the
  // native `title` attribute is mouse-only, so an icon-only control whose
  // meaning lives in a hover tooltip is unreachable from the keyboard without
  // this. The tip is a top-layer popover, so no scrolling or overflow-hidden
  // ancestor (a dialog body, a card) can clip it.
  import type { LucideIcon } from '$lib/ui/icons';

  export type IconButtonSize = 'sm' | 'md' | 'lg';
  export type IconButtonTone = 'default' | 'accent' | 'primary' | 'danger';

  const GAP = 6;
  const EDGE = 4;

  // Above the button, right-aligned to it; below when there is no room above; clamped to the viewport.
  export function placeTip(
    button: Pick<DOMRect, 'top' | 'bottom' | 'right'>,
    tip: { width: number; height: number },
    viewportWidth: number,
  ): { top: number; left: number } {
    const above = button.top - GAP - tip.height;
    return {
      top: above >= EDGE ? above : button.bottom + GAP,
      left: Math.max(EDGE, Math.min(button.right - tip.width, viewportWidth - EDGE - tip.width)),
    };
  }
</script>

<script lang="ts">
  import type { Snippet } from 'svelte';

  interface Props {
    /** Accessible name (required — the icon is decorative). */
    label: string;
    /** The icon component (a Lucide icon). Mutually exclusive with `children`. */
    icon?: LucideIcon;
    /** Click handler. */
    onclick?: (event: MouseEvent) => void;
    /** Disabled state. */
    disabled?: boolean;
    /** `sm` (28px), `md` (34px, default) or `lg` (44px, the touch target on its own). */
    size?: IconButtonSize;
    /**
     * `default` (ink), `accent` (accent-tinted glyph), `primary` (accent fill,
     * the play control), `danger` (destructive).
     */
    tone?: IconButtonTone;
    /** A circle instead of the rounded square. */
    round?: boolean;
    /** Reflects a toggle/disclosure state. */
    'aria-expanded'?: boolean;
    /** Id of the region this control discloses. */
    'aria-controls'?: string;
    /** Reflects a pressed state. */
    'aria-pressed'?: boolean;
    /** Tooltip text, shown on hover and on keyboard focus. */
    title?: string;
    /** Test hook. */
    'data-testid'?: string;
    class?: string;
    /** A plain glyph, when no icon component is wanted. */
    children?: Snippet;
  }

  let {
    label,
    icon: Icon,
    onclick,
    disabled = false,
    size = 'md',
    tone = 'default',
    round = false,
    'aria-expanded': ariaExpanded,
    'aria-controls': ariaControls,
    'aria-pressed': ariaPressed,
    title,
    'data-testid': dataTestid,
    class: klass = '',
    children,
  }: Props = $props();

  const iconPx = $derived(size === 'sm' ? 16 : size === 'lg' ? 20 : 18);

  let buttonEl = $state<HTMLButtonElement | undefined>(undefined);
  let tipEl = $state<HTMLElement | undefined>(undefined);

  function showTip(): void {
    if (!tipEl || !buttonEl || tipEl.matches(':popover-open')) return;
    tipEl.showPopover();
    const { top, left } = placeTip(buttonEl.getBoundingClientRect(), tipEl.getBoundingClientRect(), window.innerWidth);
    tipEl.style.top = `${top}px`;
    tipEl.style.left = `${left}px`;
    // A fixed tip would float loose from a scrolled button.
    window.addEventListener('scroll', hideTip, { capture: true, passive: true });
  }

  function hideTip(): void {
    window.removeEventListener('scroll', hideTip, { capture: true });
    if (tipEl?.matches(':popover-open')) tipEl.hidePopover();
  }

  $effect(() => hideTip);
</script>

<button
  bind:this={buttonEl}
  type="button"
  class="vd-iconbtn vd-iconbtn--{size} vd-iconbtn--{tone} {klass}"
  class:vd-iconbtn--round={round}
  {disabled}
  {onclick}
  aria-label={label}
  aria-expanded={ariaExpanded}
  aria-controls={ariaControls}
  aria-pressed={ariaPressed}
  data-testid={dataTestid}
  onpointerenter={title ? showTip : undefined}
  onpointerleave={title ? hideTip : undefined}
  onfocus={title ? (e) => e.currentTarget.matches(':focus-visible') && showTip() : undefined}
  onblur={title ? hideTip : undefined}
>
  {#if Icon}
    <Icon size={iconPx} strokeWidth={1.75} aria-hidden="true" focusable="false" />
  {:else if children}
    {@render children()}
  {/if}
  {#if title}
    <span bind:this={tipEl} class="vd-iconbtn__tip" popover="manual" aria-hidden="true">{title}</span>
  {/if}
</button>

<style lang="scss">
  .vd-iconbtn {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: none;
    border: 1px solid var(--line);
    border-radius: 9px;
    background: transparent;
    color: var(--ink);
    cursor: pointer;
    font-family: var(--font-ui);
    line-height: 1;
    transition:
      border-color 0.2s ease,
      background 0.2s ease,
      color 0.2s ease,
      filter 0.2s ease;

    &:hover:not(:disabled) {
      border-color: var(--accent);
      background: color-mix(in srgb, var(--accent) 6%, transparent);
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.45;
    }

    &:focus-visible {
      outline: 2px solid var(--accent-text);
      outline-offset: 2px;
    }
  }

  .vd-iconbtn--sm {
    width: 28px;
    height: 28px;
    font-size: 0.95rem;
  }

  .vd-iconbtn--md {
    width: 34px;
    height: 34px;
    font-size: 1.1rem;
  }

  .vd-iconbtn--lg {
    width: 2.75rem;
    height: 2.75rem;
    font-size: 1.25rem;
  }

  .vd-iconbtn--round {
    border-radius: 50%;
  }

  // Accent tone — the icon is tinted (the AI-guide "?" and disclosure chevrons
  // that signal "more here").
  .vd-iconbtn--accent {
    color: var(--accent-text);
  }

  // Primary tone — the accent fill, as on the primary `Button`.
  .vd-iconbtn--primary {
    border-color: var(--accent);
    background: var(--accent);
    color: var(--on-accent);

    &:hover:not(:disabled) {
      border-color: var(--accent);
      background: var(--accent);
      filter: brightness(1.08);
    }
  }

  // Destructive tone — the semantic recording-red (theme-independent), the same
  // token the danger `Button` fills with, never the practice accent. Outlined
  // rather than filled: a destructive control that is one of a pair of small
  // affordances should read as red, not shout. Hover/focus follow the red so
  // the accent never bleeds into a delete.
  .vd-iconbtn--danger {
    color: var(--color-recording-text);

    &:hover:not(:disabled) {
      border-color: var(--color-recording);
      background: color-mix(in srgb, var(--color-recording) 8%, transparent);
    }

    &:focus-visible {
      outline-color: var(--color-recording);
    }
  }

  // The tooltip: a top-layer popover placed in JS (placeTip). These override the
  // UA `[popover]` defaults (inset: 0, margin: auto, a border).
  .vd-iconbtn__tip {
    position: fixed;
    inset: auto;
    margin: 0;
    border: none;
    padding: 0.25rem 0.5rem;
    border-radius: 6px;
    background: var(--ink);
    color: var(--paper);
    font-family: var(--font-ui);
    font-size: var(--text-body);
    line-height: 1.2;
    white-space: nowrap;
    pointer-events: none;
  }
</style>
